// A2FDebugRecorder.js
//
// Per-utterance JSON debug recorder for the A2F lipsync pipeline.
// **Default ON** as of Task #31. The recorder arms automatically when
// the lab page is loaded so the user gets a saved JSON file out of a
// normal session without having to remember a URL flag. The opt-out is:
//   - load the page with ?a2fTimingDebug=0  (explicit OFF), OR
//   - run `window.a2fTimingDebug = false`  in the console
// Loading with ?a2fTimingDebug=1 is still a valid (no-op) explicit ON.
//
// On activation, every assistant utterance produces ONE JSON record
// describing the audio + frame + render timeline. The record is POSTed
// to /api/debug/a2f-recorder at end-of-utterance and is also kept in
// memory under window.latestA2FDebugSession / window.a2fDebugSessions.
//
// Task #33: the SyncV2 lipsync layer was removed. The recorder now
// describes a single rAF-driven path; no per-utterance routing flag
// exists in the schema anymore.

const SCHEMA_VERSION = 1;
const FIFO_MAX = 20;
const SAVE_ENDPOINT = "/api/debug/a2f-recorder";
const RAW_FRAMES_SAVE_ENDPOINT = "/api/debug/a2f-frames-raw";
const UTTERANCE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

// Task #41 — raw blendshape frame capture cap. The recorder stores
// every frame received from the hosted A2F server (per-utterance) in
// a sibling JSON file so the user can verify that nothing in our
// pipeline amplifies values like jawOpen. ~30fps × 60 shapes ⇒ a
// 60s reply produces ~1 MB of frame data; the cap below leaves
// generous headroom (~28 minutes at 30fps) before any silent loss
// becomes possible. When exceeded, additional frames are dropped
// and `rawA2FFramesTruncated` is set on the saved record.
const RAW_FRAMES_CAP = 50000;

// ---------------------------------------------------------------
// One-time URL flag seeding. The recorder is default-ON, so the URL
// flag exists primarily as an explicit opt-out:
//   ?a2fTimingDebug=0 → seeds window.a2fTimingDebug = false (OFF)
//   ?a2fTimingDebug=1 → seeds window.a2fTimingDebug = true  (ON; same
//                       as the default, redundant but harmless).
// Anything else / absent → leave window.a2fTimingDebug untouched, so
//                          the default-ON gate applies.
// ---------------------------------------------------------------
function _seedFlagFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search || "");
    const v = params.get("a2fTimingDebug");
    if (v === "1") {
      window.a2fTimingDebug = true;
    } else if (v === "0") {
      window.a2fTimingDebug = false;
    }
  } catch (_) {}
}
_seedFlagFromUrl();

// ---------------------------------------------------------------
// Task #274 — touch-device detection for the mobile default-OFF gate
// below. Phones/tablets (coarse pointer / multi-touch) get the
// recorder OFF by default; desktops keep the Task #31 default-ON.
// ---------------------------------------------------------------
function _isTouchDevice() {
  try {
    if (typeof window === "undefined") return false;
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches
    ) {
      return true;
    }
    return (
      "ontouchstart" in window &&
      typeof navigator !== "undefined" &&
      (navigator.maxTouchPoints || 0) > 1
    );
  } catch (_) {
    return false;
  }
}

// ---------------------------------------------------------------
// Global debug-flag gate. Read fresh each call so toggling at the
// console takes effect on the *next* utterance.
//
// Task #274: the default is now DEVICE-DEPENDENT.
//   - Desktop: default ON (unchanged Task #31 behavior — a normal
//     session yields a saved JSON without remembering a URL flag).
//   - Phones/tablets: default OFF. The recorder retains a verbatim
//     copy of every A2F frame (up to 50k/utterance), ~4/s render
//     samples, and per-chunk event logs for the WHOLE utterance,
//     then JSON.stringifys a multi-MB payload at end-of-turn and
//     keeps the last 20 finalized records pinned on `window`. That
//     per-turn retention + stringify spike is a major contributor
//     to iOS Safari force-reloading the /lab tab mid-conversation.
//     NOT deleted — load with ?a2fTimingDebug=1 (or set
//     window.a2fTimingDebug = true) to record on a phone when
//     diagnosing device-specific audio issues.
// Explicit values always win: `false` → OFF, `true` → ON, anywhere.
// ---------------------------------------------------------------
export function isA2FTimingDebugEnabled() {
  if (typeof window === "undefined") return false;
  if (window.a2fTimingDebug === false) return false;
  if (window.a2fTimingDebug === true) return true;
  return !_isTouchDevice();
}

// ---------------------------------------------------------------
// Schema factory. Always returns the eleven top-level sections so a
// freshly-allocated record is structurally valid even before any
// append happens.
// ---------------------------------------------------------------
function _emptyRecord() {
  return {
    schemaVersion: SCHEMA_VERSION,
    earlyTermination: false,
    utteranceMetadata: {
      utteranceId: null,
      startedAt: null,
      startedAtMonoMs: null,
      endedAt: null,
      endedAtMonoMs: null,
      totalDurationMs: null,
      characterId: null,
      modelId: null,
      userAgent: null,
      audioContextSampleRate: null,
      pageUrl: null,
    },
    config: {
      elevenLabsOutputFormat: null,
      sampleRate: null,
      bytesPerSample: null,
      channels: null,
      bytesPerSecond: null,
      a2fChunkSizeMs: null,
      a2fChunkSizeBytes: null,
      grpcSubChunkSizeBytes: null,
      smoothingTauMs: null,
      audioPrerollMs: null,
      blendshapePrerollMs: null,
      clockSource: null,
    },
    elevenLabsChunks: [],
    a2fBridgeChunks: [],
    a2fBrowserReceiveEvents: [],
    audioSchedulingEvents: [],
    renderSamples: [],
    warnings: [],
    // Task #11: non-dedup log of every starvation pause/resume cycle
    // for an utterance. Each entry: { phase: "pause"|"resume", ...
    // payload }. Aggregated into `summary.audioPausedForFramesCount`
    // and `summary.audioPausedForFramesTotalMs` at finalize.
    audioPauseEvents: [],
    summary: {
      totalAudioDurationMs: 0,
      totalElevenLabsChunks: 0,
      totalA2FChunks: 0,
      totalFramesReceived: 0,
      firstFrameTimeMs: null,
      lastFrameTimeMs: null,
      expectedLastFrameTimeMs: null,
      maxTimelineGapMs: 0,
      maxTimelineOverlapMs: 0,
      minFrameQueueLeadMs: null,
      avgFrameQueueLeadMs: null,
      maxAbsFrameErrorMs: null,
      avgAbsFrameErrorMs: null,
      p95AbsFrameErrorMs: null,
      countLeadBelow500: 0,
      countLeadBelow300: 0,
      countLeadBelow0: 0,
      underflowCount: 0,
      droppedFrameCount: 0,
      audioSchedulingGapCount: 0,
      // Smoking-gun for clock drift: set true when any scheduled
      // audio buffer carries `playbackStartTimeChanged === true` —
      // meaning `_timeline.wallStart` was reassigned AFTER the first
      // buffer was scheduled. Populated by Task #26's audio
      // scheduling hook + the summary derivation below.
      audioClockMismatchSuspected: false,
      smoothingTauMs: null,
      // Task #30: extended summary fields. Populated by
      // _computeSummary at finalize time. Defaulted here so
      // consumers always see a stable shape regardless of which
      // arrays were filled.
      renderSampleCount: 0,
      warningCount: 0,
      warningTypes: [],
      a2fRequiredLeadMs: null,
      syncClockCallCount: 0,
      audioBufferGapCount: 0,
      maxAudioBufferGapMs: 0,
      audioDurationMismatchObserved: false,
      // Task #11: aggregate counters for the starvation-pause gate.
      // `audioPausedForFramesCount` is the number of pause/resume
      // cycles per utterance (derived from the `resume` phase so an
      // unmatched trailing pause is not counted). `…TotalMs` is the
      // sum of `pausedForMs` across all cycles.
      audioPausedForFramesCount: 0,
      audioPausedForFramesTotalMs: 0,
      // Task #30: spec acceptance fields. Source-PCM-derived total
      // audio duration (computed from 16 kHz mono raw bytes via the
      // canonical helper), the sum of scheduled-AudioBuffer durations
      // (decoded `AudioBuffer.duration` × 1000), and the absolute
      // difference between them. A mismatch >50 ms indicates a
      // decode-path or sample-rate-conversion bug.
      totalSourcePcmDurationMs: 0,
      totalScheduledAudioDurationMs: 0,
      totalDurationMismatchMs: 0,
      finalDiagnosis: "UNKNOWN",
    },
  };
}

// ---------------------------------------------------------------
// Numeric helpers — silent on missing data so the recorder never
// throws when arrays are empty (e.g. before tasks #25/#26/#27 land).
// ---------------------------------------------------------------
function _avg(arr) {
  if (!arr || arr.length === 0) return 0;
  let s = 0;
  for (let i = 0; i < arr.length; i++) s += Number(arr[i]) || 0;
  return s / arr.length;
}
function _max(arr) {
  if (!arr || arr.length === 0) return 0;
  let m = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = Number(arr[i]);
    if (Number.isFinite(v) && v > m) m = v;
  }
  return m === -Infinity ? 0 : m;
}
function _min(arr) {
  if (!arr || arr.length === 0) return null;
  let m = Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = Number(arr[i]);
    if (Number.isFinite(v) && v < m) m = v;
  }
  return m === Infinity ? null : m;
}
function _percentile(sortedAsc, p) {
  if (!sortedAsc || sortedAsc.length === 0) return 0;
  const idx = Math.min(
    sortedAsc.length - 1,
    Math.max(0, Math.ceil((p / 100) * sortedAsc.length) - 1),
  );
  return sortedAsc[idx];
}

// ---------------------------------------------------------------
// Diagnosis classifier — ten-value vocabulary, evaluated in order,
// first match wins. See task spec for exact rules.
// ---------------------------------------------------------------
function _classify(rec) {
  const s = rec.summary || {};
  const cfg = rec.config || {};
  const bridge = Array.isArray(rec.a2fBridgeChunks) ? rec.a2fBridgeChunks : [];
  const warns = Array.isArray(rec.warnings) ? rec.warnings : [];
  const sched = Array.isArray(rec.audioSchedulingEvents)
    ? rec.audioSchedulingEvents
    : [];

  // Task #30: tightened diagnosis ordering. The new pre-rules below
  // run BEFORE the existing ones because they identify
  // recorder-level / configuration-level failures that make the
  // downstream rules unreliable. Order matters — the first match
  // wins.

  // 0a. DEBUG_RECORDER_INCOMPLETE — at least one of the four
  // primary arrays is empty when it shouldn't be. Specifically: the
  // recorder is meant to capture a full audio→bridge→browser-recv→
  // schedule pipeline, so if any of those is empty we cannot trust
  // any other diagnosis. This is the *only* condition that should
  // beat the recorder's own warnings, because those warnings
  // assume the arrays are populated.
  const elevenEmpty = !rec.elevenLabsChunks || rec.elevenLabsChunks.length === 0;
  const bridgeEmpty = !rec.a2fBridgeChunks || rec.a2fBridgeChunks.length === 0;
  const recvEmpty = !rec.a2fBrowserReceiveEvents || rec.a2fBrowserReceiveEvents.length === 0;
  const schedEmpty = !rec.audioSchedulingEvents || rec.audioSchedulingEvents.length === 0;
  const arraysMissing = [elevenEmpty, bridgeEmpty, recvEmpty, schedEmpty].filter(Boolean).length;
  // Only fire when ≥1 array is empty AND we have at least SOME data
  // (so a totally empty session falls through to UNKNOWN).
  const hasAnyData = !elevenEmpty || !bridgeEmpty || !recvEmpty || !schedEmpty;
  if (hasAnyData && arraysMissing >= 1) {
    return "DEBUG_RECORDER_INCOMPLETE";
  }

  // 0c. AUDIO_DURATION_MISMATCH — the canonical PCM-bytes-vs-
  // AudioBuffer duration check fired (per-chunk warning) OR the
  // utterance totals differ by >=50 ms (sum-level check from the
  // task's "Done looks like" criterion). Either way the decode path
  // picked the wrong sample rate somewhere; downstream timing
  // results are NOT trustable until this is fixed, so it gets
  // priority over the per-symptom rules below.
  if (
    warns.some((w) => w && w.type === "AUDIO_DURATION_MISMATCH") ||
    (Number(s.totalDurationMismatchMs) || 0) >= 50
  ) {
    return "AUDIO_DURATION_MISMATCH";
  }

  // 1. AUDIO_BUFFER_UNDERRUN
  if ((s.underflowCount || 0) > 0 || (s.audioSchedulingGapCount || 0) > 0) {
    return "AUDIO_BUFFER_UNDERRUN";
  }

  // 2. FRAME_TIMELINE_RESTART_BUG
  for (let i = 0; i < bridge.length; i++) {
    const c = bridge[i];
    if (
      c &&
      Number(c.chunkIndex) > 0 &&
      Number(c.firstFrameTimeMsAfterOffset) < 50 &&
      Number(c.chunkStartAudioMs) > 100
    ) {
      return "FRAME_TIMELINE_RESTART_BUG";
    }
  }

  // 3. FRAME_TIMELINE_OFFSET_BUG
  if ((s.maxTimelineGapMs || 0) > 50 || (s.maxTimelineOverlapMs || 0) > 50) {
    return "FRAME_TIMELINE_OFFSET_BUG";
  }

  // 4. A2F_FRAMES_LATE
  if (s.minFrameQueueLeadMs != null && s.minFrameQueueLeadMs < 0) {
    return "A2F_FRAMES_LATE";
  }
  const chunkSizeMs = Number(cfg.a2fChunkSizeMs) || 0;
  if (chunkSizeMs > 0) {
    for (let i = 0; i < bridge.length; i++) {
      const c = bridge[i];
      if (c && Number(c.processingLatencyMs) > chunkSizeMs) {
        return "A2F_FRAMES_LATE";
      }
    }
  }

  // 5. FRAME_QUEUE_UNDERRUN
  if ((s.countLeadBelow0 || 0) > 0 && (s.underflowCount || 0) === 0) {
    return "FRAME_QUEUE_UNDERRUN";
  }

  // 6. AUDIO_CLOCK_MISMATCH — either a PLAYBACK_START_TIME_CHANGED
  // warning, or syncClock was called more than once and the
  // (streamTimeMs - wallNowMs) deltas between any two calls differ by
  // more than 20 ms. The syncClock data lives in audioSchedulingEvents
  // (entries with type==="syncClock"); until task #25/26/27 fill that
  // array this branch is a no-op.
  if (warns.some((w) => w && w.type === "PLAYBACK_START_TIME_CHANGED")) {
    return "AUDIO_CLOCK_MISMATCH";
  }
  const syncClockDeltas = sched
    .filter((e) => e && e.type === "syncClock"
      && Number.isFinite(e.streamTimeMs)
      && Number.isFinite(e.wallNowMs))
    .map((e) => e.streamTimeMs - e.wallNowMs);
  if (syncClockDeltas.length >= 2) {
    let dMin = syncClockDeltas[0];
    let dMax = syncClockDeltas[0];
    for (let i = 1; i < syncClockDeltas.length; i++) {
      const v = syncClockDeltas[i];
      if (v < dMin) dMin = v;
      if (v > dMax) dMax = v;
    }
    if (dMax - dMin > 20) {
      return "AUDIO_CLOCK_MISMATCH";
    }
  }

  // 7. BROWSER_RENDER_LATE
  if (
    (s.p95AbsFrameErrorMs || 0) > 100 &&
    s.minFrameQueueLeadMs != null &&
    s.minFrameQueueLeadMs >= 0
  ) {
    return "BROWSER_RENDER_LATE";
  }

  // 8. SMOOTHING_TOO_SLOW — needs renderSamples; safe early-return
  // when renderSamples is absent / sparse.
  if (
    (s.avgAbsFrameErrorMs || 0) < 33 &&
    Number.isFinite(cfg.smoothingTauMs) &&
    Array.isArray(rec.renderSamples) &&
    rec.renderSamples.length > 4
  ) {
    // Crude lag detector: track jawOpen above/below the open
    // threshold (matches the driver's SMOOTHING_MAY_BE_TOO_SLOW
    // hook at 0.05). Reads the nested
    // `rawBlendshapeValues` / `smoothedBlendshapeValues` fields
    // that Task #27 emits, with a fallback to the legacy
    // `rawJawOpen` / `smoothedJawOpen` scalars for any pre-Task-27
    // recording. Time uses `sampledAtMonoMs` (Task #27) with `tMs`
    // legacy fallback. The driver itself raises the live
    // SMOOTHING_MAY_BE_TOO_SLOW warning; this rule mirrors the
    // logic so the per-utterance `finalDiagnosis` field stays in
    // agreement with that warning.
    const tau2 = cfg.smoothingTauMs * 2;
    const JAW_THRESH = 0.05;
    let lagFrames = 0;
    let lastRawCrossT = null;
    let lastSmoothedCrossT = null;
    let prevRawAbove = null;
    let prevSmoothedAbove = null;
    for (let i = 0; i < rec.renderSamples.length; i++) {
      const r = rec.renderSamples[i];
      if (!r) continue;
      const rawJaw =
        r.rawBlendshapeValues && Number.isFinite(r.rawBlendshapeValues.jawOpen)
          ? Number(r.rawBlendshapeValues.jawOpen)
          : Number(r.rawJawOpen) || 0;
      const smJaw =
        r.smoothedBlendshapeValues &&
        Number.isFinite(r.smoothedBlendshapeValues.jawOpen)
          ? Number(r.smoothedBlendshapeValues.jawOpen)
          : Number(r.smoothedJawOpen) || 0;
      const t = Number(r.sampledAtMonoMs ?? r.tMs) || 0;
      const rawAbove = rawJaw > JAW_THRESH;
      const smAbove = smJaw > JAW_THRESH;
      if (prevRawAbove !== null) {
        if (rawAbove !== prevRawAbove) lastRawCrossT = t;
        if (smAbove !== prevSmoothedAbove) lastSmoothedCrossT = t;
        if (
          lastRawCrossT != null &&
          lastSmoothedCrossT != null &&
          lastSmoothedCrossT - lastRawCrossT > tau2
        ) {
          lagFrames++;
        }
      }
      prevRawAbove = rawAbove;
      prevSmoothedAbove = smAbove;
    }
    if (lagFrames > 0) return "SMOOTHING_TOO_SLOW";
  }

  // 9. OK — strict spec: "none above and p95AbsFrameErrorMs <= 50".
  // For empty-array recordings (no chunks, no samples), p95AbsFrameErrorMs
  // is undefined, summary fields default to 0, and no rule above
  // triggers — so we still need rule 10 (UNKNOWN) to win that case.
  // Treating an absent p95 value as "rule does not apply" is the
  // distinguishing condition.
  if (Number.isFinite(s.p95AbsFrameErrorMs) && s.p95AbsFrameErrorMs <= 50) {
    return "OK";
  }

  // 10. UNKNOWN — empty/insufficient data falls through here.
  return "UNKNOWN";
}

// ---------------------------------------------------------------
// Summary computation — pure function over whatever arrays exist.
// Always tolerant of empty arrays.
// ---------------------------------------------------------------
function _computeSummary(rec, smoothingTauMs) {
  const s = rec.summary;
  const eleven = rec.elevenLabsChunks || [];
  const bridge = rec.a2fBridgeChunks || [];
  const recv = rec.a2fBrowserReceiveEvents || [];
  const sched = rec.audioSchedulingEvents || [];
  const samples = rec.renderSamples || [];

  s.totalElevenLabsChunks = eleven.length;
  s.totalA2FChunks = bridge.length;
  // Sum from `estimatedDurationMs` (Task #26 schema field) with a
  // fallback to `durationMs` for forward-compat with any future
  // entries that may use the shorter name.
  s.totalAudioDurationMs = eleven.reduce(
    (acc, c) =>
      acc +
      (Number(c && (c.estimatedDurationMs ?? c.durationMs)) || 0),
    0,
  );

  // Frames: prefer per-chunk frameCount or framesReceived; fall back
  // to summing per-receive-event batch sizes; last resort is the
  // count of receive events themselves. Task #30 — bridge chunks
  // store the count under `framesReceived`, not `frameCount`. Both
  // names are honored so older recordings still summarize correctly.
  let totalFrames = 0;
  for (const c of bridge) {
    totalFrames += Number(c && (c.frameCount ?? c.framesReceived)) || 0;
  }
  if (totalFrames === 0) {
    for (const e of recv) {
      totalFrames += Number(e && (e.framesInBatch ?? e.frameCount)) || 0;
    }
  }
  if (totalFrames === 0) totalFrames = recv.length;
  s.totalFramesReceived = totalFrames;

  // First / last frame timestamps from receive events. Task #26
  // populates `firstFrameTimeMs` / `lastFrameTimeMs` directly on
  // each entry (one per a2f_frames WS message); we collapse the
  // batch-level extremes into utterance-level extremes here. The
  // legacy `frameTimeMs` field name is honored as a fallback so any
  // older recordings still summarize correctly.
  if (recv.length > 0) {
    const firsts = recv
      .map((e) => Number(e && (e.firstFrameTimeMs ?? e.frameTimeMs)))
      .filter((v) => Number.isFinite(v));
    const lasts = recv
      .map((e) => Number(e && (e.lastFrameTimeMs ?? e.frameTimeMs)))
      .filter((v) => Number.isFinite(v));
    if (firsts.length > 0) s.firstFrameTimeMs = Math.min(...firsts);
    if (lasts.length > 0) s.lastFrameTimeMs = Math.max(...lasts);
  }
  // Expected last-frame: total audio duration is the natural end.
  if (s.totalAudioDurationMs > 0) {
    s.expectedLastFrameTimeMs = s.totalAudioDurationMs;
  }

  // Timeline gaps / overlaps across bridge chunks.
  let maxGap = 0;
  let maxOverlap = 0;
  for (let i = 1; i < bridge.length; i++) {
    const prev = bridge[i - 1];
    const cur = bridge[i];
    if (!prev || !cur) continue;
    const prevEnd =
      Number(prev.chunkEndAudioMs) ||
      (Number(prev.chunkStartAudioMs) || 0) +
        (Number(prev.chunkDurationMs) || 0);
    const curStart = Number(cur.chunkStartAudioMs);
    if (Number.isFinite(prevEnd) && Number.isFinite(curStart)) {
      const delta = curStart - prevEnd;
      if (delta > maxGap) maxGap = delta;
      if (-delta > maxOverlap) maxOverlap = -delta;
    }
  }
  s.maxTimelineGapMs = maxGap;
  s.maxTimelineOverlapMs = maxOverlap;

  // Frame-queue lead stats from receive events. Task #26 populates
  // `frameQueueLeadMs` per the spec; `queueLeadMs` is the legacy
  // field name kept for backward compat with any pre-Task-26
  // recordings.
  const leads = recv
    .map((e) => Number(e && (e.frameQueueLeadMs ?? e.queueLeadMs)))
    .filter((v) => Number.isFinite(v));
  if (leads.length > 0) {
    s.minFrameQueueLeadMs = _min(leads);
    s.avgFrameQueueLeadMs = _avg(leads);
    let b500 = 0,
      b300 = 0,
      b0 = 0;
    for (const v of leads) {
      if (v < 500) b500++;
      if (v < 300) b300++;
      if (v < 0) b0++;
    }
    s.countLeadBelow500 = b500;
    s.countLeadBelow300 = b300;
    s.countLeadBelow0 = b0;
  }

  // Render-error stats.
  const errs = samples
    .map((r) => Math.abs(Number(r && r.frameErrorMs) || 0))
    .filter((v) => Number.isFinite(v));
  if (errs.length > 0) {
    const sortedErrs = errs.slice().sort((a, b) => a - b);
    s.maxAbsFrameErrorMs = sortedErrs[sortedErrs.length - 1];
    s.avgAbsFrameErrorMs = _avg(errs);
    s.p95AbsFrameErrorMs = _percentile(sortedErrs, 95);
  }

  // Audio underflow + dropped-frame counts.
  // Task #27 owns these counters: every render sample stamps the
  // current per-utterance running totals (`underflowCount` and
  // `droppedFrameCount`). The summary value is therefore the
  // maximum seen across the array — i.e. the value at the last
  // stamped sample. We accept the legacy `type==="underflow"`
  // scheduling entry / `dropped` receive entry as a fallback for
  // forward-compat with any pre-Task-27 recordings (no current
  // code path produces them).
  let renderSampleUnderflow = 0;
  let renderSampleDropped = 0;
  for (const r of samples) {
    if (!r) continue;
    const u = Number(r.underflowCount);
    const d = Number(r.droppedFrameCount);
    if (Number.isFinite(u) && u > renderSampleUnderflow) renderSampleUnderflow = u;
    if (Number.isFinite(d) && d > renderSampleDropped) renderSampleDropped = d;
  }
  // Tail-underflow safety net: if the very last underflow event
  // happened AFTER the final render-sample stamp (e.g. queue
  // emptied at end-of-turn and no further sample fired), the
  // running counter on the warning's `details` carries a fresher
  // value than anything in `renderSamples`. Pick the max.
  const warnings = Array.isArray(rec.warnings) ? rec.warnings : [];
  for (const w of warnings) {
    if (w && w.type === "BROWSER_RENDER_UNDERFLOW" && w.details) {
      const wu = Number(w.details.underflowCount);
      if (Number.isFinite(wu) && wu > renderSampleUnderflow) {
        renderSampleUnderflow = wu;
      }
    }
  }
  const fallbackUnderflow = sched.filter(
    (e) => e && e.type === "underflow",
  ).length;
  const fallbackDropped = recv.filter((e) => e && e.dropped).length;
  s.underflowCount = renderSampleUnderflow || fallbackUnderflow;
  s.droppedFrameCount = renderSampleDropped || fallbackDropped;
  // Task #26 schema: every scheduled buffer carries
  // `gapFromPreviousScheduledBufferMs` (null for the first buffer).
  // Any non-trivial positive value (>5 ms tolerance for FP/timeline
  // jitter) counts as an audible scheduling gap.
  s.audioSchedulingGapCount = sched.filter(
    (e) => e && Number(e.gapFromPreviousScheduledBufferMs) > 5,
  ).length;
  // Smoking-gun for clock drift — true when `_timeline.wallStart`
  // got reassigned after the first buffer was scheduled (Task #26's
  // PLAYBACK_START_TIME_CHANGED hook flags this on the entry).
  s.audioClockMismatchSuspected = sched.some(
    (e) => e && e.playbackStartTimeChanged === true,
  );

  s.smoothingTauMs = Number.isFinite(smoothingTauMs) ? smoothingTauMs : null;

  // Task #30: extended summary fields.
  s.renderSampleCount = samples.length;
  const allWarns = Array.isArray(rec.warnings) ? rec.warnings : [];
  s.warningCount = allWarns.length;
  s.warningTypes = allWarns.map((w) => (w && w.type) || "UNKNOWN");
  s.a2fRequiredLeadMs = rec.config && Number.isFinite(rec.config.a2fRequiredLeadMs)
    ? rec.config.a2fRequiredLeadMs
    : null;
  // syncClock call count: best-effort — read the worst SYNC_CLOCK
  // warning's totalCalls (Task #30 stamps it on the warning details).
  let scCount = 1; // initial schedule call always happens
  for (const w of allWarns) {
    if (w && w.type === "SYNC_CLOCK_CALLED_MORE_THAN_ONCE" && w.details) {
      const t = Number(w.details.totalCalls);
      if (Number.isFinite(t) && t > scCount) scCount = t;
    }
  }
  s.syncClockCallCount = scCount;
  // Audio buffer gaps: count and worst-magnitude across schedule events.
  let gapCount = 0;
  let maxGapMs = 0;
  for (const e of sched) {
    const g = Number(e && e.gapFromPreviousScheduledBufferMs);
    if (Number.isFinite(g) && g > 5) {
      gapCount++;
      if (g > maxGapMs) maxGapMs = g;
    }
  }
  s.audioBufferGapCount = gapCount;
  s.maxAudioBufferGapMs = maxGapMs;
  s.audioDurationMismatchObserved = allWarns.some(
    (w) => w && w.type === "AUDIO_DURATION_MISMATCH",
  );

  // Task #11: aggregate starvation pause/resume cycles. Count
  // `resume` phases (each represents a completed cycle that has a
  // measured `pausedForMs`); sum `pausedForMs` across them. An
  // unmatched trailing `pause` (utterance ended while still paused)
  // is intentionally not counted toward TotalMs because we don't
  // have a final duration for it.
  const pauseEvents = Array.isArray(rec.audioPauseEvents)
    ? rec.audioPauseEvents
    : [];
  let pauseCycleCount = 0;
  let pauseTotalMs = 0;
  for (const e of pauseEvents) {
    if (!e || e.phase !== "resume") continue;
    pauseCycleCount++;
    const d = Number(e.pausedForMs);
    if (Number.isFinite(d)) pauseTotalMs += d;
  }
  s.audioPausedForFramesCount = pauseCycleCount;
  s.audioPausedForFramesTotalMs = pauseTotalMs;

  // Task #30 acceptance: source-PCM vs scheduled-AudioBuffer
  // duration totals, and the absolute mismatch between them.
  // `totalSourcePcmDurationMs` sums the ElevenLabs-side raw-PCM
  // durations (already computed via the canonical 16 kHz helper at
  // record time → `estimatedDurationMs` on each chunk). The
  // scheduled total reads the AudioBuffer's own `durationMs` per
  // schedule event (the only authoritative post-decode duration).
  // The "Done looks like" criterion in task-30.md is
  // `totalDurationMismatchMs < 50`.
  let totalSrc = 0;
  for (const c of eleven) {
    const d = Number(c && (c.estimatedDurationMs ?? c.durationMs));
    if (Number.isFinite(d)) totalSrc += d;
  }
  let totalSched = 0;
  for (const e of sched) {
    // Schedule events use `audioBufferDurationMs` (the post-decode
    // AudioBuffer duration in ms). `durationMs` / `bufferDurationMs`
    // are tolerated as forward-compat fallbacks.
    const d = Number(e && (e.audioBufferDurationMs ?? e.durationMs ?? e.bufferDurationMs));
    if (Number.isFinite(d)) totalSched += d;
  }
  s.totalSourcePcmDurationMs = totalSrc;
  s.totalScheduledAudioDurationMs = totalSched;
  s.totalDurationMismatchMs = Math.abs(totalSrc - totalSched);

  // Task #27 — A2F_CHUNK_PROCESSING_TOO_SLOW. Fired here (not from
  // the live driver) because the trigger is a per-bridge-chunk
  // condition that is only fully known once a chunk is closed. We
  // bypass `addWarning` because finalize sets `_active=false`
  // before _computeSummary runs (so isRecording would be false).
  // Manual dedup via the `warnings` array preserves first-wins
  // semantics. Threshold = configured `a2fChunkSizeMs` (typically
  // 200 ms) — anything above means the bridge couldn't keep up
  // with real-time and we are queue-starved by definition.
  const a2fChunkSizeMs = Number(rec.config && rec.config.a2fChunkSizeMs) || 0;
  if (a2fChunkSizeMs > 0) {
    // Worst-case-wins: scan ALL chunks and keep the one with the
    // largest `processingLatencyMs` over threshold, so the locked-
    // in warning payload is the most-diagnostic chunk for the
    // utterance (matches the worst-case dedup pattern used by the
    // driver-side warnings).
    let slowChunk = null;
    for (let i = 0; i < bridge.length; i++) {
      const c = bridge[i];
      if (!c) continue;
      const lat = Number(c.processingLatencyMs);
      if (!Number.isFinite(lat) || lat <= a2fChunkSizeMs) continue;
      if (slowChunk === null || lat > slowChunk.processingLatencyMs) {
        slowChunk = { chunkIndex: c.chunkIndex ?? i, processingLatencyMs: lat };
      }
    }
    if (
      slowChunk &&
      !rec.warnings.some((w) => w && w.type === "A2F_CHUNK_PROCESSING_TOO_SLOW")
    ) {
      rec.warnings.push({
        type: "A2F_CHUNK_PROCESSING_TOO_SLOW",
        details: {
          chunkIndex: slowChunk.chunkIndex,
          processingLatencyMs: slowChunk.processingLatencyMs,
          thresholdMs: a2fChunkSizeMs,
        },
        atMonoMs:
          typeof performance !== "undefined"
            ? performance.now()
            : Date.now(),
      });
    }
  }

  s.finalDiagnosis = _classify(rec);
}

// ---------------------------------------------------------------
// File-name helper (browser side too — handy for the manual download).
// ---------------------------------------------------------------
function _filename(utteranceId) {
  const ts = new Date()
    .toISOString()
    .replace(/[:.]/g, "-")
    .replace("Z", "Z");
  return `a2f_debug_${utteranceId}_${ts}.json`;
}

// ---------------------------------------------------------------
// The recorder. ONE instance per RealtimeLLMToElevenLabs. Holds at
// most one in-flight session at a time.
// ---------------------------------------------------------------
export class A2FDebugRecorder {
  constructor() {
    this._record = null;
    this._active = false;
    this._activeFlagSnapshot = false; // captured at beginUtterance, held constant
    this._warnedTypes = new Set(); // dedup per utterance
    // Task #41 — sibling raw-frame capture. Held outside `_record`
    // because it ships in its own JSON file and can grow much larger
    // than the timing record. Reset on every beginUtterance().
    this._rawFrameBatches = [];
    this._rawFrameCount = 0;
    this._rawFramesTruncated = false;
  }

  // True only if a session is currently being recorded AND the flag
  // captured at session start was on. Read this gate before any work.
  get isRecording() {
    return this._active && this._activeFlagSnapshot;
  }

  // Snapshot of the flag captured for *this* utterance. Lets the caller
  // skip work cheaply without re-reading window state.
  get flagSnapshot() {
    return this._activeFlagSnapshot;
  }

  // -----------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------

  beginUtterance(meta, config) {
    // If a previous session is still open (unusual — typically means a
    // hard cancel that didn't route through finalize), force-finalize
    // it as early-termination so we don't lose the diagnostic file.
    if (this._active) {
      try {
        this.finalize({ earlyTermination: true });
      } catch (_) {}
    }

    // Capture flag now and hold for the whole utterance.
    const flagOn = isA2FTimingDebugEnabled();
    this._activeFlagSnapshot = flagOn;
    this._warnedTypes = new Set();

    if (!flagOn) {
      // Flag off → no record allocated. All append methods become no-ops.
      this._record = null;
      this._active = false;
      return null;
    }

    const rec = _emptyRecord();
    const m = meta || {};
    rec.utteranceMetadata.utteranceId = String(m.utteranceId || "0");
    rec.utteranceMetadata.startedAt = new Date().toISOString();
    rec.utteranceMetadata.startedAtMonoMs =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    rec.utteranceMetadata.characterId = m.characterId || null;
    rec.utteranceMetadata.modelId = m.modelId || null;
    rec.utteranceMetadata.userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent || null : null;
    rec.utteranceMetadata.audioContextSampleRate =
      Number(m.audioContextSampleRate) || null;
    rec.utteranceMetadata.pageUrl =
      typeof window !== "undefined" && window.location
        ? window.location.href
        : null;

    Object.assign(rec.config, config || {});

    // Task #41 — reset the sibling raw-frame buffer.
    this._rawFrameBatches = [];
    this._rawFrameCount = 0;
    this._rawFramesTruncated = false;

    this._record = rec;
    this._active = true;
    return rec;
  }

  /**
   * Task #41 — append one a2f_frames batch's RAW frames (exactly as
   * received from the bridge / hosted A2F server, with the bridge's
   * 4-decimal rounding but no smoothing, no clamping, no scaling).
   * Caller passes the bridge's `frames` array unmodified plus a few
   * routing fields so an analyst can correlate batches with the
   * existing `a2fBrowserReceiveEvents` entries by `messageIndex` /
   * `chunkIndex`. Frames beyond `RAW_FRAMES_CAP` for the utterance
   * are dropped and `rawA2FFramesTruncated` is set so the analyst
   * sees the truncation explicitly rather than silent loss.
   */
  addRawA2FFrames(entry) {
    if (!this.isRecording) return;
    if (!entry || !Array.isArray(entry.frames) || entry.frames.length === 0) {
      return;
    }
    const remaining = RAW_FRAMES_CAP - this._rawFrameCount;
    if (remaining <= 0) {
      this._rawFramesTruncated = true;
      return;
    }
    let srcFrames = entry.frames;
    if (srcFrames.length > remaining) {
      srcFrames = srcFrames.slice(0, remaining);
      this._rawFramesTruncated = true;
    }
    // Wrap each frame so it carries an explicit index inside its
    // batch AND a running utterance-wide index. The bridge frames
    // themselves don't carry an index — analysts need both to align
    // a raw dump against per-chunk timing records and against the
    // rendered output. The original `time_ms` and full `blendshapes`
    // map are preserved verbatim alongside the indexes.
    const baseIdx = this._rawFrameCount;
    const wrapped = new Array(srcFrames.length);
    for (let i = 0; i < srcFrames.length; i++) {
      const f = srcFrames[i] || {};
      wrapped[i] = {
        frameIndexInBatch: i,
        frameIndexInUtterance: baseIdx + i,
        time_ms: f.time_ms,
        blendshapes: f.blendshapes,
      };
    }
    this._rawFrameBatches.push({
      messageIndex: Number.isFinite(entry.messageIndex) ? entry.messageIndex : null,
      chunkIndex: Number.isFinite(entry.chunkIndex) ? entry.chunkIndex : null,
      receivedAtMonoMs: Number.isFinite(entry.receivedAtMonoMs)
        ? entry.receivedAtMonoMs
        : null,
      frames: wrapped,
    });
    this._rawFrameCount += wrapped.length;
  }

  // -----------------------------------------------------------
  // Append methods — no-ops when not recording. Tasks #25/#26/#27
  // call these from the audio + frame paths.
  // -----------------------------------------------------------

  addElevenLabsChunk(entry) {
    if (!this.isRecording) return;
    this._record.elevenLabsChunks.push(entry);
  }
  addA2FBridgeChunk(entry) {
    if (!this.isRecording) return;
    this._record.a2fBridgeChunks.push(entry);
  }
  addA2FBrowserReceiveEvent(entry) {
    if (!this.isRecording) return;
    this._record.a2fBrowserReceiveEvents.push(entry);
  }
  addAudioSchedulingEvent(entry) {
    if (!this.isRecording) return;
    this._record.audioSchedulingEvents.push(entry);
  }
  addRenderSample(entry) {
    if (!this.isRecording) return;
    this._record.renderSamples.push(entry);
  }

  /**
   * Task #11: append a non-deduped audio pause/resume event. Unlike
   * `addWarning` (one entry per type per utterance), every starvation
   * pause and resume is captured so multi-cycle utterances are
   * faithfully reported. `entry` should carry `phase: "pause"` or
   * `"resume"` plus the same payload the warning already carries.
   */
  addAudioPauseEvent(entry) {
    if (!this.isRecording) return;
    if (!entry) return;
    this._record.audioPauseEvents.push(entry);
  }

  addWarning(type, details) {
    if (!this.isRecording) return;
    if (!type) return;
    if (this._warnedTypes.has(type)) return;
    this._warnedTypes.add(type);
    this._record.warnings.push({
      type: String(type),
      details: details == null ? null : details,
      atMonoMs:
        typeof performance !== "undefined" ? performance.now() : Date.now(),
    });
  }

  /**
   * Worst-case-wins variant of `addWarning`. First call creates the
   * entry; subsequent calls REPLACE the entry's `details` only when
   * `isWorse(existingDetails, candidateDetails)` returns true. The
   * `type` itself is still deduped (one warning per type per
   * session), but the locked-in payload reflects the worst sample
   * for the utterance — required by Task #27 so e.g. a
   * `FRAME_ERROR_OVER_100` entry carries the largest absErr seen,
   * not just the first crossing. Falls through to plain dedup if
   * `isWorse` isn't supplied.
   */
  upsertWarning(type, details, isWorse) {
    if (!this.isRecording) return;
    if (!type) return;
    if (!this._warnedTypes.has(type)) {
      this._warnedTypes.add(type);
      this._record.warnings.push({
        type: String(type),
        details: details == null ? null : details,
        atMonoMs:
          typeof performance !== "undefined"
            ? performance.now()
            : Date.now(),
      });
      return;
    }
    if (typeof isWorse !== "function") return;
    // Locate the existing entry (linear scan — warnings list is
    // bounded by the dedup set, typically <= 12 entries per
    // utterance, so this is cheap).
    for (let i = 0; i < this._record.warnings.length; i++) {
      const w = this._record.warnings[i];
      if (w && w.type === type) {
        try {
          if (isWorse(w.details, details)) {
            w.details = details == null ? null : details;
          }
        } catch (_) {}
        return;
      }
    }
  }

  // -----------------------------------------------------------
  // Finalize. Computes summary, runs classifier, persists, fans out
  // to the global inspection surfaces, and clears in-memory state.
  // Safe to call repeatedly; second call is a no-op.
  // -----------------------------------------------------------

  finalize({
    earlyTermination = false,
    smoothingTauMs = null,
    viaPageUnload = false,
  } = {}) {
    if (!this._active) return null;
    const rec = this._record;
    this._active = false;
    if (!rec) {
      this._record = null;
      return null;
    }

    rec.earlyTermination = !!earlyTermination;
    rec.utteranceMetadata.endedAt = new Date().toISOString();
    rec.utteranceMetadata.endedAtMonoMs =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (
      Number.isFinite(rec.utteranceMetadata.startedAtMonoMs) &&
      Number.isFinite(rec.utteranceMetadata.endedAtMonoMs)
    ) {
      rec.utteranceMetadata.totalDurationMs =
        rec.utteranceMetadata.endedAtMonoMs -
        rec.utteranceMetadata.startedAtMonoMs;
    }

    // Make sure smoothingTau reflects whatever the driver currently
    // reports, even if begin() didn't have it yet.
    if (
      Number.isFinite(smoothingTauMs) &&
      rec.config.smoothingTauMs == null
    ) {
      rec.config.smoothingTauMs = smoothingTauMs;
    }

    try {
      _computeSummary(rec, rec.config.smoothingTauMs);
    } catch (e) {
      // Never throw out of finalize. If summary blows up we still
      // want to ship the raw record so the user can debug.
      try {
        console.warn("[A2FDebug] summary computation failed:", e);
      } catch (_) {}
    }

    // Task #30: per-utterance summary table. Compact `console.table`
    // output of the headline numbers so the user gets at-a-glance
    // verification of the run without opening the saved JSON. Falls
    // back to console.log when console.table isn't available.
    try {
      const s = rec.summary || {};
      const cfg = rec.config || {};
      const row = {
        diagnosis: s.finalDiagnosis,
        requiredLeadMs: cfg.a2fRequiredLeadMs,
        elevenChunks: s.totalElevenLabsChunks,
        bridgeChunks: s.totalA2FChunks,
        recvEvents: (rec.a2fBrowserReceiveEvents || []).length,
        schedEvents: (rec.audioSchedulingEvents || []).length,
        renderSamples: s.renderSampleCount,
        warnings: s.warningCount,
        warningTypes: (s.warningTypes || []).join(","),
        audioMs: Math.round(s.totalAudioDurationMs || 0),
        srcPcmMs: Math.round(s.totalSourcePcmDurationMs || 0),
        schedMs: Math.round(s.totalScheduledAudioDurationMs || 0),
        durMismatchMs: Math.round(s.totalDurationMismatchMs || 0),
        framesRecv: s.totalFramesReceived,
        minLeadMs: s.minFrameQueueLeadMs,
        avgLeadMs: s.avgFrameQueueLeadMs,
        p95ErrMs: s.p95AbsFrameErrorMs,
        underflows: s.underflowCount,
        gapCount: s.audioBufferGapCount,
        maxGapMs: s.maxAudioBufferGapMs,
        durMismatch: s.audioDurationMismatchObserved,
        syncClockCalls: s.syncClockCallCount,
      };
      if (typeof console !== "undefined" && typeof console.table === "function") {
        console.groupCollapsed("[A2FDebug] utterance summary");
        console.table([row]);
        console.groupEnd();
      } else if (typeof console !== "undefined" && typeof console.log === "function") {
        console.log("[A2FDebug] utterance summary", row);
      }
    } catch (_) {
      // Pure observability — never let table rendering block finalize.
    }

    // Update inspection globals.
    try {
      if (typeof window !== "undefined") {
        // Lazy-init: covers the case where the user toggled the flag
        // on AFTER module-load (so the eager init at the bottom of the
        // file was skipped) but is now committing a real recording.
        _ensureInspectionGlobals();
        window.latestA2FDebugSession = rec;
        window.a2fDebugSessions.push(rec);
        while (window.a2fDebugSessions.length > FIFO_MAX) {
          window.a2fDebugSessions.shift();
        }
      }
    } catch (_) {}

    // Task #41 — generate ONE filename timestamp token here so the
    // timing record file and the sibling raw-frames file land on disk
    // with byte-identical `_{timestampToken}` suffixes. The previous
    // approach let each Flask route mint its own `time.strftime(...)`
    // a few ms apart, which broke strict filename pairing. Format
    // matches the server's: `YYYYMMDDTHHMMSS-mmmZ`. Server validates
    // and falls back to its own minted token if this is missing or
    // doesn't match the regex.
    const _now = new Date();
    const _pad = (n, w = 2) => String(n).padStart(w, "0");
    const timestampToken =
      `${_now.getUTCFullYear()}${_pad(_now.getUTCMonth() + 1)}${_pad(_now.getUTCDate())}` +
      `T${_pad(_now.getUTCHours())}${_pad(_now.getUTCMinutes())}${_pad(_now.getUTCSeconds())}` +
      `-${_pad(_now.getUTCMilliseconds(), 3)}Z`;

    // POST to the save endpoint. Failure is logged once and otherwise
    // ignored — the in-memory record + downloadA2FDebugJson() still work.
    //
    // Two fetch shapes (Task #31):
    //   - In-page finalize (viaPageUnload=false, the common path):
    //     plain fetch with NO `keepalive`. The browser caps `keepalive`
    //     bodies at ~64 KB and post-Task-#30 records routinely exceed
    //     150 KB (full receive-event + render-sample arrays), so the
    //     keepalive shape silently fails with a TypeError before the
    //     POST ever leaves the page.
    //   - Page-unload finalize (viaPageUnload=true, from beforeunload /
    //     pagehide via shutdown()): keepalive: true so the request can
    //     outlive the page. The body may still hit the 64 KB cap and
    //     fail; that's a known best-effort path. Normal in-page
    //     finalize (audio drain or barge-in cancel) is the reliable
    //     persistence path and uses the unbounded plain fetch.
    try {
      const payload = {
        utteranceId: String(
          rec.utteranceMetadata.utteranceId || "0",
        ).slice(0, 64),
        // Shared with the raw-frames POST below so both files land
        // on disk with the same `_{timestampToken}` suffix.
        timestampToken,
        record: rec,
      };
      if (typeof fetch === "function") {
        const init = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        };
        if (viaPageUnload) {
          init.keepalive = true;
        }
        fetch(SAVE_ENDPOINT, init)
          .then((res) => {
            if (!res || !res.ok) {
              try {
                console.warn(
                  "[A2FDebug] save endpoint returned non-2xx:",
                  res && res.status,
                );
              } catch (_) {}
            }
          })
          .catch((err) => {
            try {
              console.warn(
                viaPageUnload
                  ? "[A2FDebug] keepalive save failed (page-unload path; body may exceed 64 KB cap):"
                  : "[A2FDebug] save endpoint failed:",
                err,
              );
            } catch (_) {}
          });
      }
    } catch (e) {
      try {
        console.warn("[A2FDebug] failed to post debug record:", e);
      } catch (_) {}
    }

    // Task #41 — POST the sibling raw-frames file. Same shape as the
    // timing record save above (utteranceId + opaque payload), but
    // routed to a different endpoint so the server can apply a larger
    // size cap and write a separately-named file. We skip the POST
    // entirely when no frames were captured (e.g. early-termination
    // before any a2f_frames batch arrived).
    try {
      if (this._rawFrameBatches.length > 0 && typeof fetch === "function") {
        const utteranceId = String(
          rec.utteranceMetadata.utteranceId || "0",
        ).slice(0, 64);
        const rawPayload = {
          utteranceId,
          // Shared with the timing-record POST above; server uses
          // this for the filename suffix so the two files pair
          // exactly on disk.
          timestampToken,
          rawFrames: {
            timestampToken,
            schemaVersion: SCHEMA_VERSION,
            utteranceId,
            startedAt: rec.utteranceMetadata.startedAt,
            endedAt: rec.utteranceMetadata.endedAt,
            characterId: rec.utteranceMetadata.characterId,
            modelId: rec.utteranceMetadata.modelId,
            totalBatches: this._rawFrameBatches.length,
            totalFrames: this._rawFrameCount,
            truncated: this._rawFramesTruncated,
            cap: RAW_FRAMES_CAP,
            // The bridge applies 4-decimal rounding before forwarding;
            // these frames carry NO smoothing, NO clamping, NO scaling
            // applied by anything in our pipeline. See comment block in
            // a2f_bridge.py and A2FLipSyncDriver.js for the full path.
            note:
              "Frame values are exactly as received from the hosted A2F " +
              "server (rounded to 4 decimals by the bridge for transport). " +
              "No client-side amplification, smoothing, or clamping has " +
              "been applied to these values.",
            batches: this._rawFrameBatches,
          },
        };
        const init = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(rawPayload),
        };
        if (viaPageUnload) {
          init.keepalive = true;
        }
        fetch(RAW_FRAMES_SAVE_ENDPOINT, init)
          .then((res) => {
            if (!res || !res.ok) {
              try {
                console.warn(
                  "[A2FDebug] raw-frames save returned non-2xx:",
                  res && res.status,
                );
              } catch (_) {}
            }
          })
          .catch((err) => {
            try {
              console.warn(
                viaPageUnload
                  ? "[A2FDebug] keepalive raw-frames save failed (page-unload path; body may exceed 64 KB cap):"
                  : "[A2FDebug] raw-frames save failed:",
                err,
              );
            } catch (_) {}
          });
      }
    } catch (e) {
      try {
        console.warn("[A2FDebug] failed to post raw-frames record:", e);
      } catch (_) {}
    }

    // Drop the raw-frame buffer references so we don't pin a multi-MB
    // payload in memory after the POST is in flight.
    this._rawFrameBatches = [];
    this._rawFrameCount = 0;
    this._rawFramesTruncated = false;

    this._record = null;
    return rec;
  }
}

// ---------------------------------------------------------------
// Manual-download helper. Exposed on window so a developer can pull
// any of the last 20 sessions out of the console without reload.
// ---------------------------------------------------------------
function _downloadJson(utteranceId) {
  if (typeof window === "undefined") return false;
  const sessions = Array.isArray(window.a2fDebugSessions)
    ? window.a2fDebugSessions
    : [];
  let target = null;
  if (utteranceId == null) {
    target = window.latestA2FDebugSession || sessions[sessions.length - 1];
  } else {
    const idStr = String(utteranceId);
    for (let i = sessions.length - 1; i >= 0; i--) {
      const s = sessions[i];
      if (s && s.utteranceMetadata && String(s.utteranceMetadata.utteranceId) === idStr) {
        target = s;
        break;
      }
    }
  }
  if (!target) {
    try {
      console.warn("[A2FDebug] no session found for download (id=", utteranceId, ")");
    } catch (_) {}
    return false;
  }
  try {
    const id = String((target.utteranceMetadata || {}).utteranceId || "0");
    const blob = new Blob([JSON.stringify(target, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = _filename(id);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (e) {
    try {
      console.warn("[A2FDebug] download failed:", e);
    } catch (_) {}
    return false;
  }
}

// ---------------------------------------------------------------
// Wire the inspection globals when the debug flag is actually on.
// As of Task #31 the flag is default-ON, so this eager wire-up runs
// on every normal page load; the only path that skips it is an
// explicit opt-out (`?a2fTimingDebug=0` or `window.a2fTimingDebug =
// false` set BEFORE this module loads). We also wire lazily inside
// finalize() so a console toggle (`window.a2fTimingDebug = true`
// after a previous opt-out) takes effect on the next utterance.
// ---------------------------------------------------------------
function _ensureInspectionGlobals() {
  if (typeof window === "undefined") return;
  if (!Array.isArray(window.a2fDebugSessions)) {
    window.a2fDebugSessions = [];
  }
  if (typeof window.latestA2FDebugSession === "undefined") {
    window.latestA2FDebugSession = null;
  }
  if (typeof window.downloadA2FDebugJson !== "function") {
    window.downloadA2FDebugJson = _downloadJson;
  }
  // Convenience: expose the gate too so other modules / the console
  // can ask "would the next utterance be recorded?".
  if (typeof window.isA2FTimingDebugEnabled !== "function") {
    window.isA2FTimingDebugEnabled = isA2FTimingDebugEnabled;
  }
}

if (typeof window !== "undefined" && isA2FTimingDebugEnabled()) {
  _ensureInspectionGlobals();
}

// Re-export the regex so the Flask side and the browser stay in sync.
export const UTTERANCE_ID_PATTERN = UTTERANCE_ID_RE;
